import type {
  CreateLegalObligationExclusionBody,
  LegalDocumentComplianceResponse,
  LegalDocumentImminentResponse,
  LegalDocumentSitesResponse,
  LegalObligationAnnualResponse,
  LegalDocumentVisibilityResponse,
  LegalObligationInstanceLocation,
  LegalObligationsResponse,
} from '@safetyhub/shared';
import { api, type ApiResponse } from './api';

/**
 * 법정 서류 관리 — 고객 화면 입구의 API 클라이언트(줄기 `legal-document-obligation-management` 항목 9).
 *
 * 응답 형상의 정본은 shared `features/legal-documents.ts`이고 **여기서 고쳐 쓰지 않는다** — 서버가
 * 그 타입을 바꾸면 이 모듈과 화면이 tsc로 따라온다.
 *
 * ⛔ **모든 호출이 `?companyId=<현재 작업 조직 id>` query를 싣는다** — 서버 `OrganizationGuard`가
 * 멤버십을 URL param·query로만 붙이고(body는 믿지 않는다) 권한 키(`obligation.view`·`obligation.exempt`)를
 * 그 조직 자격으로 판정한다. 빠지면 400이다. 그래서 `companyId`를 **첫 인자 필수**로 두어 tsc가 막게 했다
 * (선례 `hooks/useInspections`의 `getInspections(companyId, …)`).
 *
 * ⚠️ 이 모듈은 화면 spec이 `vi.mock`하는 대상이다 — 그래서 **순수 술어·라벨을 여기 두지 않는다**
 * (팩토리 mock은 돌려준 키만 존재해 새 export가 그 spec들을 `No "X" export` 로 죽인다).
 * 라벨은 `legalDocumentLabels.ts`가 소유한다.
 *
 * ⛔ 플래그(`legal-obligation`) 게이트는 **서버 입구의 몫**이다(결정 ⓓ 「화면만 숨기면 API는 열려
 * 있다」). 화면은 `getLegalDocumentsVisibility`를 메뉴·페이지 거울로만 쓴다.
 */

/**
 * react-query 키 — 무효화가 한 자리를 보게 모은다. 조직을 바꾸면 답이 바뀌므로(권한·접근이 그 조직
 * 자격이다) 전부 `companyId`를 품는다.
 */
export const legalDocumentsQueryKeys = {
  all: ['legal-documents'] as const,
  visibility: (companyId: string) => ['legal-documents', companyId, 'visibility'] as const,
  sites: (companyId: string) => ['legal-documents', companyId, 'sites'] as const,
  obligations: (companyId: string, siteId: string) =>
    ['legal-documents', companyId, 'sites', siteId, 'obligations'] as const,
  instanceLocation: (companyId: string, instanceId: string) =>
    ['legal-documents', companyId, 'instances', instanceId] as const,
  imminent: (companyId: string) => ['legal-documents', companyId, 'imminent'] as const,
  compliance: (companyId: string) => ['legal-documents', companyId, 'compliance'] as const,
  /** `year` 미지정(= 서버가 현장 시간대의 올해로)은 `'current'`로 둔다 — 지정한 해와 캐시가 섞이지 않게 */
  annual: (companyId: string, siteId: string, year: number | undefined) =>
    ['legal-documents', companyId, 'sites', siteId, 'annual', year ?? 'current'] as const,
};

function unwrap<T>(body: ApiResponse<T>): T {
  if (!body.success || body.data === null) {
    // 봉투 실패는 axios 인터셉터가 대개 먼저 reject한다 — 여기는 200인데 data가 빈 방어선이다
    throw body;
  }
  return body.data;
}

function sitePath(siteId: string): string {
  return `/legal-documents/sites/${encodeURIComponent(siteId)}`;
}

export async function getLegalDocumentsVisibility(
  companyId: string
): Promise<LegalDocumentVisibilityResponse> {
  const { data } = await api.get<ApiResponse<LegalDocumentVisibilityResponse>>(
    '/legal-documents/visibility',
    { params: { companyId } }
  );
  return unwrap(data);
}

export async function getLegalDocumentSites(
  companyId: string
): Promise<LegalDocumentSitesResponse> {
  const { data } = await api.get<ApiResponse<LegalDocumentSitesResponse>>(
    '/legal-documents/sites',
    {
      params: { companyId },
    }
  );
  return unwrap(data);
}

export async function getLegalObligations(
  companyId: string,
  siteId: string
): Promise<LegalObligationsResponse> {
  const { data } = await api.get<ApiResponse<LegalObligationsResponse>>(
    `${sitePath(siteId)}/obligations`,
    { params: { companyId } }
  );
  return unwrap(data);
}

/**
 * 알림 클릭 목적지 — 인스턴스 id → 현장·의무(알림은 현장을 싣지 않는다). 없음·남의 것·플래그 off는
 * 서버가 **가르지 않고 전부 404**다(shared `LegalObligationInstanceLocation` doc).
 */
export async function getLegalObligationInstanceLocation(
  companyId: string,
  instanceId: string
): Promise<LegalObligationInstanceLocation> {
  const { data } = await api.get<ApiResponse<LegalObligationInstanceLocation>>(
    `/legal-documents/instances/${encodeURIComponent(instanceId)}`,
    { params: { companyId } }
  );
  return unwrap(data);
}

export async function createLegalObligationExclusion(
  companyId: string,
  siteId: string,
  body: CreateLegalObligationExclusionBody
): Promise<void> {
  await api.post(`${sitePath(siteId)}/exclusions`, body, { params: { companyId } });
}

/**
 * 기관 전체 기한이 다가오는 문서(L1) — 접근 가능한 (플래그가 켜진) 현장 전수의 기한이 다가오거나 지난 미이행.
 * 창(이번 달 말 ∨ 알림 창의 끝 · 현장 시간대)은 서버가 정한다(shared `LegalDocumentImminentItem` doc).
 */
export async function getLegalDocumentImminent(
  companyId: string
): Promise<LegalDocumentImminentResponse> {
  const { data } = await api.get<ApiResponse<LegalDocumentImminentResponse>>(
    '/legal-documents/imminent',
    { params: { companyId } }
  );
  return unwrap(data);
}

/**
 * 사업장 이행률 요약(L20) — 접근 가능한 (플래그가 켜진) 현장 전수의 이번 기간 이행 수. 통·분모의 정의는
 * shared `LegalDocumentComplianceCounts` docblock이 정본이다(서버가 의무 목록 행을 그대로 센다).
 */
export async function getLegalDocumentCompliance(
  companyId: string
): Promise<LegalDocumentComplianceResponse> {
  const { data } = await api.get<ApiResponse<LegalDocumentComplianceResponse>>(
    '/legal-documents/compliance',
    { params: { companyId } }
  );
  return unwrap(data);
}

/** 한 현장의 연간 격자(L2) — `year`를 빼면 서버가 현장 시간대의 올해로 답한다 */
export async function getLegalObligationAnnualGrid(
  companyId: string,
  siteId: string,
  year?: number
): Promise<LegalObligationAnnualResponse> {
  const { data } = await api.get<ApiResponse<LegalObligationAnnualResponse>>(
    `${sitePath(siteId)}/annual`,
    { params: year === undefined ? { companyId } : { companyId, year } }
  );
  return unwrap(data);
}

export async function revokeLegalObligationExclusion(
  companyId: string,
  siteId: string,
  exclusionId: string
): Promise<void> {
  await api.delete(`${sitePath(siteId)}/exclusions/${encodeURIComponent(exclusionId)}`, {
    params: { companyId },
  });
}
